/**
 * iter201 — useGroupPrefetch(groupId)
 *
 * When a user opens a group detail page, eagerly fire a batch of GET
 * requests for the data they're most likely to need next so the SW can
 * cache it. Subsequent reads (even offline) then hit the cache.
 *
 * The actual caching is done by the SW's fetch handler (every GET to
 * /api/* with a 200 response is cached) — this hook just *triggers*
 * those requests. We deliberately:
 *   • Use api.get() with `__bg: true` to mark these as background reads
 *     so any global "loading…" spinner ignores them
 *   • Run with a tiny stagger so we don't blast the network in parallel
 *   • Tolerate failures silently (offline = nothing to prefetch anyway)
 */
import { useEffect } from 'react';
import api from '../lib/api';

const STAGGER_MS = 120;
// Real GET routes (verified against backend routers). Expenses + members
// are embedded inside the group detail response, so a single /groups/{id}
// call warms them too. Fund + sessions live on their own routes.
const ENDPOINTS = (groupId) => [
  `/groups/${groupId}`,
  `/groups/${groupId}/fund`,
  `/groups/${groupId}/sessions`,
];

export default function useGroupPrefetch(groupId) {
  useEffect(() => {
    if (!groupId) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    let cancelled = false;
    const endpoints = ENDPOINTS(groupId);
    (async () => {
      for (let i = 0; i < endpoints.length; i += 1) {
        if (cancelled) return;
        const ep = endpoints[i];
        // Fire and forget — the SW does the actual caching
        api.get(ep, { __bg: true }).catch(() => {});
        // Small stagger so we don't compete with the user's own fetches
        await new Promise((r) => setTimeout(r, STAGGER_MS));
      }
    })();
    return () => { cancelled = true; };
  }, [groupId]);
}
